/**
 * P4-03: the AI reads the passport data page in the background; the advisor checks every field
 * against the document, then confirms (the expiry date and the profile are filled from it).
 */

import {
  ApiError,
  type DossierItem,
  type PassportValuesRequest,
  useImmigrationItemsConfirmPassportCreate,
  useImmigrationItemsReadPassportCreate,
} from "@agencyos/api-client";
import { Badge, Button, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { dateText } from "../billing/shared";

const FIELDS = [
  "family_name",
  "given_names",
  "passport_number",
  "nationality",
  "birth_date",
  "issue_date",
  "expiry_date",
  "sex",
] as const;
type Field = (typeof FIELDS)[number];
type Values = Record<Field, string>;
const DATES: readonly Field[] = ["birth_date", "issue_date", "expiry_date"];

/** `extracted.read` / `extracted.confirmed` are free JSON: keep only the expected strings. */
function valuesFrom(extracted: unknown, part: "read" | "confirmed"): Values {
  const source = extracted && typeof extracted === "object" ? (extracted as Record<string, unknown>)[part] : undefined;
  const record = source && typeof source === "object" ? (source as Record<string, unknown>) : {};
  const out = {} as Values;
  for (const field of FIELDS) {
    const value = record[field];
    out[field] = typeof value === "string" ? value : "";
  }
  return out;
}

function errorText(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  return error.detail ?? Object.values(fieldErrors(error))[0];
}

function ConfirmForm({ item, onDone }: { item: DossierItem; onDone: () => void }) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Values>(() => valuesFrom(item.extracted, "read"));
  const confirm = useImmigrationItemsConfirmPassportCreate({ mutation: { onSuccess: onDone } });
  const errors = fieldErrors(confirm.error);
  const data: PassportValuesRequest = { ...values };
  return (
    <div className="stack">
      <p role="note" className="ko">
        {t("ee.passport.checkNotice")}
      </p>
      <div className="form-grid">
        {FIELDS.map((field) => (
          <TextField
            key={field}
            label={t(`ee.passport.fields.${field}`)}
            type={DATES.includes(field) ? "date" : "text"}
            value={values[field]}
            error={errors[field]}
            required={field === "passport_number" || field === "expiry_date"}
            onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
          />
        ))}
      </div>
      <div className="row">
        <Button
          disabled={confirm.isPending || !values.passport_number.trim() || !values.expiry_date}
          onClick={() => confirm.mutate({ id: item.id, data })}
        >
          {confirm.isPending ? t("common.loading") : t("ee.passport.confirm")}
        </Button>
      </div>
      {confirm.isError && !errors.passport_number && !errors.expiry_date ? (
        <p role="alert" className="ko">
          {errorText(confirm.error) ?? t("common.error")}
        </p>
      ) : null}
    </div>
  );
}

export function PassportReading({ item }: { item: DossierItem }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({
      predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/immigration/"),
    });
  const read = useImmigrationItemsReadPassportCreate({ mutation: { onSuccess: refresh } });
  const status = item.extraction_status;
  const confirmed = status === "confirmed" ? valuesFrom(item.extracted, "confirmed") : undefined;

  return (
    <div className="stack">
      {status === "pending" ? (
        <p role="status" aria-live="polite" className="muted">
          {t("ee.passport.pending")}
        </p>
      ) : null}
      {status === "to_confirm" ? (
        <>
          <Badge tone="offer">{t("ee.passport.toConfirm")}</Badge>
          <ConfirmForm key={`${item.id}-to_confirm`} item={item} onDone={refresh} />
        </>
      ) : null}
      {confirmed ? (
        <p className="ok">
          <Badge tone="success">{t("ee.passport.confirmedBadge")}</Badge>{" "}
          {t("ee.passport.confirmed", {
            number: confirmed.passport_number,
            expiry: dateText(confirmed.expiry_date, i18n.language),
          })}
        </p>
      ) : null}
      {status === "failed" ? (
        <p role="alert" className="ko">
          {t("ee.passport.failed")}
        </p>
      ) : null}
      {status !== "pending" && status !== "to_confirm" ? (
        <div className="row">
          <Button variant="secondary" disabled={read.isPending} onClick={() => read.mutate({ id: item.id })}>
            {read.isPending ? t("common.loading") : status ? t("ee.passport.readAgain") : t("ee.passport.read")}
          </Button>
        </div>
      ) : null}
      {read.isError ? (
        <p role="alert" className="ko">
          {errorText(read.error) ?? t("common.error")}
        </p>
      ) : null}
    </div>
  );
}
